import { useState } from 'react';
import { plural, t, Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';
import _ from 'lodash';
import Button from '@material-ui/core/Button';
import { SELECTIONMODE_MULTI } from '../../data-table/SelectionContext';
import DataTable from '../../data-table/DataTable';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import { useQueryLoader } from '../../data-table/Loaders';
import AdminPage from '../AdminPage';
import Actions from '../../page-framework/actions/Actions';
import { useFormDeleteService, useFormDeleteWrapper } from '../../../services/FormDeleteService';
import ConfirmDeleteButton from '../../form/ConfirmDeleteButton';
import {
    CONFIGURED_AGENTS_COUNT_FIELD,
    DESCRIPTION_FIELD,
    IS_DEFAULT_TRANSLATED_FIELD,
    NAME_FIELD,
    PROFILE_NAME_FIELD,
    NETWORKS_IN_USE_ERROR,
    EAP_TYPE_FIELD,
} from './WiredNetworksConstants';
import { SORT_ASC, useSuccessfulAction } from '../../../utilities/FormUtilities';
import { buildCreatePageTarget, buildPageId, buildPageTarget } from '../AdminLinkUtils';
import { AdminPages } from '../AdminPages';
import Link from '../../links/Link';
import useUiAlerts, { createAlertConfig, ERROR } from '../../../stores/AlertsStore';
import { GENERIC_SERVER_ERROR } from '../../../services/FormErrorService';
import { useIntervalRefreshTrigger } from '../../data-table/Triggers';

const functionName = AdminPages.WiredNetworks;

function collectDeleteErrors(deleteErrors) {
    let genericError = false;
    const errors = [];

    errors.push(
        i18n._(
            plural(deleteErrors.length, {
                one: `${deleteErrors.length} Wired network was not deleted because of the following error:`,
                other: `${deleteErrors.length} Wired network were not deleted because of the following error(s):`,
            })
        )
    );

    const errTypes = new Set(_.map(deleteErrors, (error) => error.userMessageKey));
    const networkErrors = _.filter(deleteErrors, (error) => error.userMessageKey === NETWORKS_IN_USE_ERROR);

    if (errTypes.size >= 2) {
        errors.push(
            i18n._(t`Wired network could not be deleted because they are in use. for example: {example}`.id, {
                example: _.first(networkErrors).userMessage,
            })
        );
    } else if (networkErrors.length > 2) {
        errors.push(
            i18n._(
                t`Wired network could not be deleted because they are part of a Wired connect test and/or an nPoint Wired network. for example: {example}`
                    .id,
                { example: _.first(networkErrors).userMessage }
            )
        );
    } else {
        // Loop through and build out our errors.
        _.each(deleteErrors, (error) => {
            if (error && error.userMessageKey === NETWORKS_IN_USE_ERROR) {
                errors.push(error.userMessage);
            } else if (!genericError) {
                genericError = true;
                errors.push(i18n._(GENERIC_SERVER_ERROR));
            }
        });
    }

    return errors;
}

function getColumns() {
    return [
        {
            type: COLUMN_TYPES.DRILLINLINK,
            mapping: PROFILE_NAME_FIELD,
            title: t`Profile Name`,
            sortable: true,
            width: 280,
            link: {
                target: buildPageTarget(functionName),
            },
        },
        {
            type: COLUMN_TYPES.TEXT,
            mapping: EAP_TYPE_FIELD,
            title: t`EAP Type`,
            sortable: true,
            width: 190,
        },
        {
            type: COLUMN_TYPES.TEXT,
            mapping: IS_DEFAULT_TRANSLATED_FIELD,
            title: t`Default Wired Network`,
            sortable: true,
            width: 190,
        },
        {
            type: COLUMN_TYPES.TEXT,
            mapping: CONFIGURED_AGENTS_COUNT_FIELD,
            title: t`Assigned nPoints`,
            sortable: true,
            width: 160,
        },
    ];
}

const initialSort = [{ property: PROFILE_NAME_FIELD, direction: SORT_ASC }];

const dataSource = {
    url: '/query/admin/wiredNetworks',
    crudUrl: '/ipm/v1/admin/wiredNetworks',
    totalProperty: 'count',
    dataProperty: 'data',
    params: {
        searchFields: [NAME_FIELD, DESCRIPTION_FIELD, PROFILE_NAME_FIELD, EAP_TYPE_FIELD],
    },
};

export default function WiredNetworksList() {
    const formDeleteService = useFormDeleteService();
    const { deleteWrapper } = useFormDeleteWrapper();
    const handleSuccessfulDelete = useSuccessfulAction();
    const [, uiAlertActions] = useUiAlerts();
    const [actionsDisabled, setActionsDisabled] = useState(false);
    const [tableState, setTableState] = useState(undefined);
    const [tableSelection, setTableSelection] = useState(undefined);
    const deleteUrl = `${dataSource.crudUrl}/selection/remove`;
    const { triggerRef, trigger: refresher } = useIntervalRefreshTrigger();

    // Used by the data table to load data.
    const loader = useQueryLoader(dataSource, { disableFilterState: true });

    const postDeleteHandler = (serverResponse) => {
        const { response } = serverResponse;
        const deleteErrors = _.chain(response)
            .filter({ deleted: false })
            .map((r) => _.get(r, 'error'))
            .value();

        if (_.isEmpty(deleteErrors)) {
            return handleSuccessfulDelete(response, null, refresher);
        }

        uiAlertActions.createAlert(createAlertConfig(ERROR, collectDeleteErrors(deleteErrors).join('  ')));
    };

    const handleConfirmedDelete = () => {
        const params = _.merge(
            {
                search: tableState?.search || '',
            },
            dataSource.params
        );

        return deleteWrapper({
            deleteFunction: () => formDeleteService.deleteSelection(deleteUrl, params, tableSelection),
            refresherFunc: refresher,
            postDeleteHandler,
        })();
    };

    const handleTableSelectionChange = (currentTableSelection) => {
        setTableSelection(currentTableSelection);
        setActionsDisabled(
            !currentTableSelection || (_.isEmpty(currentTableSelection.selection) && !currentTableSelection.inverted)
        );
    };

    return (
        <AdminPage
            title={t`Wired network`}
            pageId={buildPageId(functionName)}
            actions={
                <Actions>
                    <ConfirmDeleteButton
                        disabled={actionsDisabled}
                        handleConfirmedDelete={handleConfirmedDelete}
                        plural={tableSelection?.selection?.length > 1}
                    />
                    <Button
                        component={Link}
                        to={buildCreatePageTarget(functionName)}
                        variant="contained"
                        color="primary"
                    >
                        <Trans>Create</Trans>
                    </Button>
                </Actions>
            }
        >
            <DataTable
                tableId="admin-wired-network-list"
                loader={loader}
                triggerRef={triggerRef}
                columns={getColumns()}
                visibleRows={0}
                showSelectionColumn
                sorters={initialSort}
                onStateChanged={setTableState}
                selectionOptions={{
                    mode: SELECTIONMODE_MULTI,
                    allowSelectAll: true,
                    idMapping: '_id',
                    onChange: handleTableSelectionChange,
                }}
            />
        </AdminPage>
    );
}
